import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { FiberManualRecord } from "@mui/icons-material";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";
import { Link } from "react-router-dom";

const severityColor = (sev) => {
  const s = (sev || "").toString().toLowerCase();
  if (s.includes("crit")) return "orange";
  if (s.includes("high")) return "orange";
  return "gold";
};

export default function LiveThreatFeed({ filters }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildParams = () => {
    const params = {};
    if (filters?.firsttime) params.firsttime = filters.firsttime;
    if (filters?.lasttime) params.lasttime = filters.lasttime;
    if (filters?.status) params.status = filters.status;
    if (filters?.severity) params.severity = filters.severity;
    return params;
  };

  useEffect(() => {
    const fetchIncidents = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await apiClient.get(API_CONFIG.ENDPOINTS.INCIDENTS, {
          params: {
            ...buildParams(),
            status: filters?.status || undefined,
            severity: filters?.severity || undefined,
          },
          headers: { Accept: "application/json" },
        });
        const list = Array.isArray(data) ? data : [];
        const filtered = list
          .filter(
            (i) =>
              (i.status || i.Status || "").toString().toLowerCase() !== "closed" &&
              ["critical", "high"].includes(
                (i.severity || i.Severity || "").toString().toLowerCase()
              )
          )
          .sort(
            (a, b) =>
              new Date(b.lastActivityTime || b.updatedAt || b.createdAt || 0) -
              new Date(a.lastActivityTime || a.updatedAt || a.createdAt || 0)
          )
          .slice(0, 3);
        setIncidents(filtered);
      } catch (err) {
        console.error("Failed to load incidents", err);
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchIncidents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const entries = useMemo(
    () =>
      incidents.map((i) => ({
        type: i.title || i.Title || "Incident",
        severity: i.severity || i.Severity || "unknown",
        severityColor: severityColor(i.severity || i.Severity),
        time:
          i.lastActivityTime ||
          i.updatedAt ||
          i.createdAt ||
          i.timeGenerated ||
          i.TimeGenerated ||
          "",
        source: i.source || i.SourceSystem || i.sourceSystem || "—",
        target: i.providerName || i.ProviderName || i.providerIncidentId || "—",
        linkId:
          i.incidentId ||
          i.incidentUUID ||
          i.id ||
          i.incidentNumber ||
          i.incidentName,
      })),
    [incidents]
  );

  return (
    <Box className="live-threat-feed">
      <Box className="live-threat-feed__header">
        <Box>
          <Typography variant="h5" className="dashboard__widget-title">
            Top Threat Detections
          </Typography>
          <Typography variant="body2" className="dashboard__widget-subtitle">
          Highlights the most critical and frequently detected security threats across your environment.
          </Typography>
        </Box>
        {/* <Box className="live-threat-feed__live-indicator">
          <FiberManualRecord sx={{ color: '#39ff14', fontSize: 8 }} />
          <Typography variant="body2" className="live-threat-feed__live-text">
            Live
          </Typography>
        </Box> */}
      </Box>

      <Box className="live-threat-feed__list">
        {loading && <div className="text-muted">Loading incidents…</div>}
        {error && <div className="text-muted">Failed to load: {error}</div>}
        {!loading && !error && !entries.length && (
          <div className="text-muted">No incidents found.</div>
        )}
        {!loading &&
          !error &&
          entries.map((threat, index) => (
            <Box key={index} className="live-threat-feed__item">
              <Box className="live-threat-feed__item-content">
                <Box className="live-threat-feed__item-left">
                  <Box className="live-threat-feed__item-header">
                  {threat.linkId ? (
                    <Link
                      to={`/incidents/${threat.linkId}`}
                      className="live-threat-feed__threat-type link-unstyled"
                    >
                      {threat.type}
                    </Link>
                  ) : (
                    <Typography variant="h6" className="live-threat-feed__threat-type">
                      {threat.type}
                    </Typography>
                  )}
                    <Chip
                      label={threat.severity}
                      size="small"
                      sx={{
                        backgroundColor: `rgba(${
                          threat.severityColor === "orange" ? "255,165,0" : "255,215,0"
                        }, 0.08)`,
                        color: threat.severityColor,
                        border: `1.875px solid ${threat.severityColor}`,
                        fontSize: "12px",
                        fontWeight: 400,
                        height: "29px",
                        textTransform: "uppercase",
                        borderRadius: "2px",
                      }}
                    />
                  </Box>
                  <Box className="live-threat-feed__item-details">
                    <Box className="live-threat-feed__detail-row">
                      <Typography variant="body2" className="live-threat-feed__detail-label">
                        Source:
                      </Typography>
                      <Typography variant="body2" className="live-threat-feed__detail-value">
                        {threat.source}
                      </Typography>
                    </Box>
                    <Box className="live-threat-feed__detail-row">
                      <Typography variant="body2" className="live-threat-feed__detail-label">
                        Target:
                      </Typography>
                      <Typography variant="body2" className="live-threat-feed__detail-value">
                        {threat.target}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box className="live-threat-feed__item-right">
                  <Typography variant="body2" className="live-threat-feed__time">
                    {threat.time ? new Date(threat.time).toLocaleTimeString() : "—"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
}
